/**
 * LiveNow — public "what's live right now" grid for lamirage.live/live
 *
 * Account-less: uses plain fetch, no auth token.
 * Polls GET /api/live/public/live-now every 30 s.
 *
 * Design: black/gold — pageDarkGrad + GlowOrbs (no blue left).
 * Uses shared LiveUI kit for all components.
 *
 * Each card links to:
 *   - https://<subdomain>.lamirage.live   (if subdomain set)
 *   - /w/<slug>                            (fallback)
 */

import React, { useState, useEffect } from "react";
import { LIVE, goldGrad, goldGradText, pageDarkGrad } from "@/lib/liveTheme";
import {
  GlowOrbs,
  GoldShimmerText,
  GoldButton,
  GlassCard,
  LiveBadge,
  SectionLabel,
  GoldRule,
} from "@/components/live/LiveUI";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LiveNowEntry {
  slug: string;
  subdomain: string | null;
  coupleNames: string | null;
  coverImageKey: string | null;
  themeColor: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function coverSrc(key: string | null): string | undefined {
  if (!key) return undefined;
  if (key.startsWith("http://") || key.startsWith("https://")) return key;
  if (key.startsWith("/")) return key;
  return `/api/media/${encodeURIComponent(key)}`;
}

function watchUrl(entry: LiveNowEntry): string {
  if (entry.subdomain) {
    return `https://${entry.subdomain}.lamirage.live`;
  }
  return `/w/${entry.slug}`;
}

// ---------------------------------------------------------------------------
// Gold spinner (consistent with LiveWatch)
// ---------------------------------------------------------------------------

function GoldSpinner() {
  return (
    <div
      role="status"
      aria-label="Loading"
      style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: `2px solid ${LIVE.goldBorder}`,
        borderTopColor: LIVE.gold,
        animation: "lnSpin 0.9s linear infinite",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function StreamCard({ entry }: { entry: LiveNowEntry }) {
  const src = coverSrc(entry.coverImageKey ?? null);
  const href = watchUrl(entry);

  return (
    <a
      href={href}
      style={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 16,
        overflow: "hidden",
        textDecoration: "none",
        background: LIVE.bgCard,
        border: `1px solid ${LIVE.goldBorder}`,
        transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
        position: "relative",
      }}
      onMouseEnter={e => {
        const el = e.currentTarget as HTMLAnchorElement;
        el.style.transform = "translateY(-5px)";
        el.style.boxShadow = `0 12px 40px rgba(201,169,98,0.18), 0 4px 16px rgba(0,0,0,0.5)`;
        el.style.borderColor = LIVE.goldBorderStrong;
      }}
      onMouseLeave={e => {
        const el = e.currentTarget as HTMLAnchorElement;
        el.style.transform = "";
        el.style.boxShadow = "";
        el.style.borderColor = LIVE.goldBorder;
      }}
      aria-label={`Watch ${entry.coupleNames ?? "Wedding"} live`}
    >
      {/* Gold top accent hairline */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: goldGrad,
          borderRadius: "16px 16px 0 0",
          zIndex: 2,
        }}
      />

      {/* 16:9 cover image */}
      <div
        style={{
          position: "relative",
          width: "100%",
          paddingBottom: "56.25%",
          background: LIVE.bgDeep,
          overflow: "hidden",
        }}
      >
        {src ? (
          <img
            src={src}
            alt={entry.coupleNames ?? "Wedding"}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: 0.72,
              transition: "opacity 0.2s, transform 0.3s ease",
            }}
            onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
            onMouseEnter={e => { (e.target as HTMLImageElement).style.transform = "scale(1.04)"; }}
            onMouseLeave={e => { (e.target as HTMLImageElement).style.transform = ""; }}
          />
        ) : (
          /* Elegant gold fallback — no emoji */
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `radial-gradient(ellipse at center, rgba(201,169,98,0.08) 0%, ${LIVE.bgDeep} 75%)`,
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                border: `1.5px solid ${LIVE.goldBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: goldGrad,
                  opacity: 0.7,
                }}
              />
            </div>
          </div>
        )}

        {/* Gradient overlay for text legibility */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to bottom, rgba(10,10,9,0.10) 40%, rgba(10,10,9,0.65) 100%)",
          }}
        />

        {/* LIVE badge — top left */}
        <div style={{ position: "absolute", top: 12, left: 12, zIndex: 3 }}>
          <LiveBadge status="live" />
        </div>
      </div>

      {/* Info row */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          padding: "1.25rem 1.25rem 1.5rem",
          gap: 12,
        }}
      >
        <h2
          className="font-playfair"
          style={{
            fontSize: "1.1rem",
            fontWeight: 600,
            color: "#fff",
            lineHeight: 1.3,
            margin: 0,
            letterSpacing: "-0.01em",
          }}
        >
          {entry.coupleNames ?? "Wedding Ceremony"}
        </h2>

        {/* Watch CTA */}
        <div style={{ marginTop: "auto" }}>
          <GoldButton href={href} variant="solid" aria-label={`Watch ${entry.coupleNames ?? "wedding"} live`}>
            Watch live
            <svg
              aria-hidden="true"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              style={{ flexShrink: 0 }}
            >
              <path
                d="M1 6h10M7 2l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </GoldButton>
        </div>
      </div>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 16,
        padding: "5rem 2rem",
        textAlign: "center",
      }}
    >
      {/* Decorative gold ring */}
      <div
        aria-hidden="true"
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          border: `1.5px solid ${LIVE.goldBorder}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 4,
        }}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: goldGrad,
            opacity: 0.45,
          }}
        />
      </div>

      <p
        className="font-playfair"
        style={{
          fontSize: "1.2rem",
          fontStyle: "italic",
          color: "rgba(255,255,255,0.65)",
          margin: 0,
          lineHeight: 1.4,
        }}
      >
        No weddings are live right now.
      </p>

      <p
        style={{
          fontSize: "0.85rem",
          color: LIVE.textMuted,
          margin: 0,
          maxWidth: 280,
          lineHeight: 1.6,
        }}
      >
        Check back soon — a celebration is just around the corner.
      </p>

      <GoldRule />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function LiveNow() {
  const [entries, setEntries] = useState<LiveNowEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLiveNow = async () => {
    try {
      const res = await fetch("/api/live/public/live-now");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: LiveNowEntry[] = await res.json();
      setEntries(data);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveNow();
    const id = setInterval(fetchLiveNow, 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: pageDarkGrad, color: "#fff", fontFamily: "Inter, system-ui, sans-serif" }}
    >
      {/* Keyframes */}
      <style>{`
        @keyframes lnSpin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Ambient glow orbs */}
      <GlowOrbs />

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1.25rem 2rem",
          borderBottom: `1px solid ${LIVE.goldBorder}`,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          background: "rgba(10,10,9,0.80)",
        }}
      >
        <a
          href="/"
          style={{
            ...goldGradText,
            fontFamily: "'Playfair Display', Georgia, serif",
            fontWeight: 700,
            fontSize: "0.85rem",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            textDecoration: "none",
          }}
        >
          LA Mirage Live
        </a>

        <a
          href="/"
          style={{
            fontFamily: "Inter, system-ui, sans-serif",
            fontSize: "0.72rem",
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: LIVE.gold,
            textDecoration: "none",
            opacity: 0.7,
            transition: "opacity 0.15s",
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; }}
          onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.7"; }}
        >
          ← Home
        </a>
      </nav>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header
        style={{
          position: "relative",
          zIndex: 10,
          textAlign: "center",
          padding: "3rem 1.5rem 2.5rem",
        }}
      >
        <SectionLabel>Happening now</SectionLabel>

        <h1
          className="font-playfair live-fade-up"
          style={{
            fontSize: "clamp(2rem, 5.5vw, 3.2rem)",
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            marginTop: "0.75rem",
            marginBottom: "0.75rem",
          }}
        >
          <GoldShimmerText>Weddings Live Now</GoldShimmerText>
        </h1>

        <p
          style={{
            fontSize: "0.85rem",
            color: LIVE.textMuted,
            marginTop: "0.5rem",
            letterSpacing: "0.02em",
          }}
        >
          Updates every 30 seconds
        </p>

        <div style={{ marginTop: "1.25rem", display: "flex", justifyContent: "center" }}>
          <GoldRule />
        </div>
      </header>

      {/* ── Main content ────────────────────────────────────────────────── */}
      <main
        style={{
          position: "relative",
          zIndex: 10,
          flex: 1,
          padding: "0 1.25rem 3rem",
          maxWidth: 1100,
          width: "100%",
          margin: "0 auto",
        }}
      >
        {/* Loading */}
        {loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "5rem 0",
            }}
          >
            <GoldSpinner />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <GlassCard style={{ maxWidth: 400, margin: "3rem auto", padding: "2rem", textAlign: "center" }}>
            <p style={{ fontSize: "0.875rem", color: LIVE.textMuted, lineHeight: 1.6 }}>
              Unable to load live streams. Please try again shortly.
            </p>
          </GlassCard>
        )}

        {/* Empty */}
        {!loading && !error && entries.length === 0 && (
          <GlassCard style={{ maxWidth: 520, margin: "2rem auto" }}>
            <EmptyState />
          </GlassCard>
        )}

        {/* Cards grid */}
        {!loading && !error && entries.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))",
              gap: "1.5rem",
            }}
          >
            {entries.map(entry => (
              <StreamCard key={entry.slug} entry={entry} />
            ))}
          </div>
        )}
      </main>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer
        style={{
          position: "relative",
          zIndex: 10,
          textAlign: "center",
          padding: "1.5rem 1.5rem",
          borderTop: `1px solid ${LIVE.goldBorder}`,
        }}
      >
        <p style={{ fontSize: "0.72rem", color: LIVE.textMuted }}>
          &copy; {new Date().getFullYear()} LA Mirage Luxury Venue &middot;{" "}
          <a
            href="https://lamirage.co"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: `rgba(201,169,98,0.55)`, textDecoration: "none" }}
          >
            lamirage.co
          </a>
        </p>
      </footer>
    </div>
  );
}
